import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { seoProposeAction, seoQueues, seoValidateDraft } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, num, pct } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/ctr")({
  head: () => ({ meta: [{ title: "CTR opportunities · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: CtrPage,
});

function pathOf(url: string) {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function CtrPage() {
  const load = useServerFn(seoQueues);
  const q = useQuery({ queryKey: ["seo-queues"], queryFn: () => load() });
  const d = q.data as any;
  const list = (d?.ctr ?? []) as any[];

  return (
    <SeoPage
      title="Click-through opportunities"
      description="Pages that appear in search often but get clicked less than their position would suggest. Any wording you propose is checked against advertising and mortgage rules before it can be used."
    >
      {q.isLoading ? (
        <Loading />
      ) : list.length === 0 ? (
        <Empty>
          Nothing to show yet — this needs Search Console data with at least 50 impressions on a page before it can spot a
          shortfall.
        </Empty>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Opportunities" value={num(list.length)} />
            <Stat label="Estimated clicks missed / month" value={num(list.reduce((s, c) => s + c.missedClicks, 0))} />
            <Stat label="Best single opportunity" value={num(list[0]?.missedClicks ?? 0)} />
          </div>
          <div className="mt-6 grid gap-3">
            {list.slice(0, 40).map((c) => (
              <Opportunity key={`${c.page}:${c.query}`} item={c} />
            ))}
          </div>
        </>
      )}
    </SeoPage>
  );
}

function Opportunity({ item }: { item: any }) {
  const validateFn = useServerFn(seoValidateDraft);
  const proposeFn = useServerFn(seoProposeAction);
  const [draft, setDraft] = useState("");
  const [qa, setQa] = useState<any>(null);
  const targetPath = pathOf(item.page);

  const validate = useMutation({
    mutationFn: () => validateFn({ data: { actionType: "meta_title", after: draft, targetPath } }),
    onSuccess: (res: any) => setQa(res),
    onError: (e: any) => toast.error(e?.message ?? "Could not check this wording"),
  });

  const propose = useMutation({
    mutationFn: () =>
      proposeFn({
        data: {
          actionType: "meta_title",
          targetPath,
          after: draft,
          evidence: { query: item.query, position: item.position, missedClicks: item.missedClicks },
        },
      }),
    onSuccess: (res: any) =>
      res?.status === "blocked"
        ? toast.error(res.reason ?? "Blocked")
        : toast.success(`Saved for review (risk: ${res?.risk})`),
    onError: (e: any) => toast.error(e?.message ?? "Could not save"),
  });

  return (
    <div className="rounded-2xl border border-border bg-background p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{targetPath}</p>
        <Pill tone="warn">{`${num(item.missedClicks)} clicks/month missed`}</Pill>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Search: “{item.query}” · position {item.position.toFixed(1)} · {num(item.impressions)} impressions ·{" "}
        {pct(item.ctr)} clicked vs {pct(item.expectedCtr)} typical.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setQa(null);
          }}
          placeholder="Proposed search title"
          className="max-w-xl"
        />
        <Button size="sm" variant="outline" onClick={() => validate.mutate()} disabled={!draft || validate.isPending}>
          Check wording
        </Button>
        <Button size="sm" onClick={() => propose.mutate()} disabled={!draft || propose.isPending}>
          Send for approval
        </Button>
      </div>
      {qa ? (
        <div className="mt-2 text-sm">
          <Pill tone={qa.qa.ok ? "good" : "bad"}>{qa.qa.ok ? "Passes checks" : "Cannot be used"}</Pill>{" "}
          <Pill tone={qa.risk.risk === "high" ? "bad" : qa.risk.risk === "medium" ? "warn" : "good"}>
            {`${qa.risk.risk} risk`}
          </Pill>
          {qa.qa.issues?.length ? (
            <ul className="mt-2 list-disc pl-5 text-destructive">
              {qa.qa.issues.map((i: string) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          ) : null}
          {qa.qa.warnings?.length ? (
            <ul className="mt-2 list-disc pl-5 text-muted-foreground">
              {qa.qa.warnings.map((i: string) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
